/**
 * Grid de categorias inline com cards de produto — 7ª ilha.
 *
 * Substitui o `categoriesHtml` do V1 `renderHome()` (delivery_app.html
 * ~2964-3046). Paridade visual exata: mesma estrutura de h2 + card
 * com imagem à direita.
 *
 * Cada card renderiza:
 *  - Imagem
 *  - Nome + badge "Recomendado" se highlighted
 *  - Descrição
 *  - Preço (com strikethrough/badge -X% se em promo)
 *  - Stepper +/- INLINE pra produtos simples (mesma logic da
 *    ProductSteppersIsland — paridade)
 *  - Esgotado: card opaco + badge vermelho
 *
 * Click no card → `window.openProductDetail(id)` (wizard V1)
 * EXCETO em produto simples (stepper +/- já adiciona, não abre wizard).
 *
 * Stepper integrado direto aqui (não usa portal) — mais simples e
 * performático que ter mount points separados. ProductSteppersIsland
 * fica desativado quando ProductCardsIsland está ativo.
 */
import { memo, useSyncExternalStore } from 'react';
import type { CartLine, MenuCategory, MenuProduct } from '../types';
import { haptic } from '../utils/haptic';
import { guardCanAddToCart } from '../utils/storeOpenGuard';
import { track } from '../utils/tracking';
import { CollapsibleListGate } from './_CollapsibleListGate';
import { useI18n } from '../i18n';

// Quantos itens de cada categoria aparecem de cara na home. O resto fica
// atrás de um "Ver mais N itens" — lojas com muitos sabores (50+ pizzas)
// deixavam a home pesadíssima renderizando TODOS os cards de uma vez.
const CATEGORY_ITEM_CAP = 20;

declare global {
  interface Window {
    CART?: CartLine[];
    openProductDetail?: (productId: number) => void;
  }
}

// ─── Produto simples check (paridade V1) ──────────────────────────────
type ProductWithConfig = MenuProduct & {
  config?: {
    flexible_rules?: unknown[];
    flexible_groups?: unknown[];
    fixed_items?: unknown[];
    combo_type?: string;
  } | string;
  combo_items?: unknown[];
  detailed_description?: string;
};

function isSimpleProduct(p: ProductWithConfig): boolean {
  if (!p) return false;
  if (p.is_pizza) return false;
  if (p.is_active === false) return false;
  if ((Number(p.price) || 0) <= 0) return false;
  if (p.size_prices && Object.keys(p.size_prices).length > 0) return false;
  let cfg = p.config || {};
  if (typeof cfg === 'string') {
    try { cfg = JSON.parse(cfg); } catch { cfg = {}; }
  }
  const c = cfg as { flexible_rules?: unknown[]; flexible_groups?: unknown[]; fixed_items?: unknown[]; combo_type?: string };
  const flex = (Array.isArray(c.flexible_rules) && c.flexible_rules.length > 0)
    || (Array.isArray(c.flexible_groups) && c.flexible_groups.length > 0)
    || c.combo_type === 'flexible';
  const fixedItems = (Array.isArray(c.fixed_items) && c.fixed_items.length > 0)
    || (Array.isArray(p.combo_items) && p.combo_items.length > 0)
    || c.combo_type === 'fixed';
  return !(flex || fixedItems);
}

// ─── Cart helpers (paridade V1) ───────────────────────────────────────
function isSimpleCartLine(it: CartLine): boolean {
  if (!it || it.type !== 'product') return false;
  if (it.parts && it.parts.length > 0) return false;
  if (it.addons && it.addons.length > 0) return false;
  if (it.combo_data && it.combo_data.length > 0) return false;
  if (it.removed_ingredients && it.removed_ingredients.length > 0) return false;
  if (it.observation && String(it.observation).trim().length > 0) return false;
  return true;
}

function findSimpleLineIndex(cart: CartLine[], productId: number): number {
  return cart.findIndex((it) => it.product_id === productId && isSimpleCartLine(it));
}

function subscribeCart(cb: () => void) {
  window.addEventListener('aliv:cart-updated', cb);
  return () => window.removeEventListener('aliv:cart-updated', cb);
}

function getSimpleQty(productId: number): number {
  const cart = window.CART || [];
  return cart
    .filter((it) => it.product_id === productId && isSimpleCartLine(it))
    .reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);
}

function addSimpleToCart(prod: ProductWithConfig) {
  if (prod.is_active === false) return;
  // Loja fechada → bloqueia o add (a menos que tenha agendamento). Ver cardápio
  // segue liberado; a trava é só aqui no momento de adicionar.
  if (!guardCanAddToCart()) return;
  haptic('tick');
  const cart = (window.CART = window.CART || []);
  const idx = findSimpleLineIndex(cart, prod.id);
  if (idx >= 0) {
    cart[idx].quantity = (Number(cart[idx].quantity) || 0) + 1;
  } else {
    cart.push({
      _tempId: Date.now() + Math.random(),
      product_id: prod.id,
      name: prod.name,
      type: 'product',
      quantity: 1,
      price: Number(prod.price) || 0,
      price_in_points: 0,
      observation: '',
      size_id: null,
      size_name: '',
      size_slices: 0,
      parts: [],
      addons: [],
      combo_data: [],
      details: [],
      removed_ingredients: [],
      removed_names: [],
      desc_resume: prod.name,
      offer_items: [],
    });
  }
  (window as Window & { updateCartUI?: () => void }).updateCartUI?.();
  // GTM/Meta: add_to_cart também na ADIÇÃO RÁPIDA (botão + do card). Antes só
  // o wizard disparava → o + do card não contava como add_to_cart.
  const _v = Number(prod.price) || 0;
  track('add_to_cart', {
    currency: 'BRL',
    value: _v,
    ecommerce: {
      currency: 'BRL',
      value: _v,
      items: [{ item_id: String(prod.id), item_name: prod.name ?? '', price: _v, quantity: 1 }],
    },
  });
}

function removeSimpleFromCart(productId: number) {
  const cart = window.CART || [];
  const idx = findSimpleLineIndex(cart, productId);
  if (idx < 0) return;
  haptic('tick');
  cart[idx].quantity = (Number(cart[idx].quantity) || 0) - 1;
  if (cart[idx].quantity <= 0) cart.splice(idx, 1);
  (window as Window & { updateCartUI?: () => void }).updateCartUI?.();
}

// ─── Stepper inline (subcomponente do card) ──────────────────────────
const InlineStepper = memo(function InlineStepper({ product }: { product: ProductWithConfig }) {
  // `memo` + props só com `product`: sem o hook, trocar de idioma não
  // re-renderizaria o stepper e "Adicionar" ficaria em português no meio
  // de um card já traduzido.
  const { t } = useI18n();
  const qty = useSyncExternalStore(
    subscribeCart,
    () => getSimpleQty(product.id),
    () => 0,
  );

  if (qty <= 0) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          addSimpleToCart(product);
        }}
        className="bg-red-600 hover:bg-red-700 active:bg-red-800 text-white px-3 py-1.5 rounded-full shadow-sm font-bold text-xs transition inline-flex items-center gap-1.5 active:scale-95"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <i className="fas fa-plus text-[10px]" /> {t('Adicionar')}
      </button>
    );
  }

  return (
    <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full pl-1 pr-1 py-1 shadow-sm">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          removeSimpleFromCart(product.id);
        }}
        className="bg-white hover:bg-red-100 text-red-600 w-7 h-7 rounded-full flex items-center justify-center font-bold transition active:scale-95"
        style={{ WebkitTapHighlightColor: 'transparent' }}
        aria-label={t('Diminuir')}
      >
        <i className="fas fa-minus text-xs" />
      </button>
      <span className="text-sm font-extrabold text-red-700 text-center tabular-nums" style={{ minWidth: '1.5rem' }}>
        {qty}
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          addSimpleToCart(product);
        }}
        className="bg-red-600 hover:bg-red-700 text-white w-7 h-7 rounded-full flex items-center justify-center font-bold transition active:scale-95"
        style={{ WebkitTapHighlightColor: 'transparent' }}
        aria-label={t('Aumentar')}
      >
        <i className="fas fa-plus text-xs" />
      </button>
    </div>
  );
});

// ─── PriceBlock (paridade V1) ────────────────────────────────────────
function fmtBR(v: number): string {
  return v.toFixed(2).replace('.', ',');
}

const PriceDisplay = memo(function PriceDisplay({ product }: { product: ProductWithConfig }) {
  const { t } = useI18n();
  const price = Number(product.price) || 0;
  const original = Number(product.original_price) || 0;
  const hasSizes = !!product.size_prices && Object.keys(product.size_prices).length > 0;

  if (original > price && price > 0) {
    const pct = Math.round((1 - price / original) * 100);
    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-sm font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded border border-red-100">
          R$ {fmtBR(price)}
        </span>
        <span className="text-xs font-medium text-gray-500 line-through decoration-2 decoration-red-500/70">
          R$ {fmtBR(original)}
        </span>
        <span className="text-[10px] font-extrabold text-white bg-green-600 px-1.5 py-0.5 rounded shadow-sm">
          -{pct}%
        </span>
      </div>
    );
  }

  if (price === 0) {
    return (
      <span className="text-[10px] font-bold text-orange-600 uppercase tracking-widest bg-orange-50 px-2 py-1 rounded border border-orange-200 shadow-sm inline-flex items-center gap-1">
        <i className="fas fa-sliders-h" /> {t('A escolher')}
      </span>
    );
  }

  const prefix = product.is_pizza || hasSizes ? `${t('A partir de')} ` : '';
  return (
    <span className="text-sm font-bold text-gray-900 bg-gray-100 px-2 py-1 rounded">
      {prefix}R$ {fmtBR(price)}
    </span>
  );
});

// ─── ProductCard ─────────────────────────────────────────────────────
const ProductCard = memo(function ProductCard({ product }: { product: ProductWithConfig }) {
  const { t, tr } = useI18n();
  const isSoldOut = product.is_active === false;
  const simple = isSimpleProduct(product);

  const onCardClick = () => {
    if (isSoldOut) return;
    // Click no card vai DIRETO pro wizard — sem passo intermediário de
    // teaser. O `openProductDetail` (sobrescrito pelo bootstrap) já roteia:
    // simples → WizardSimpleIsland; pizza/combo → V1.
    if (typeof window.openProductDetail === 'function') {
      window.openProductDetail(product.id);
    }
  };

  return (
    <div
      onClick={onCardClick}
      className={[
        'bg-white p-4 rounded-xl shadow-sm border flex justify-between items-start mb-3 lg:mb-0 relative transition h-full',
        isSoldOut
          ? 'border-gray-200 opacity-60 grayscale pointer-events-none'
          : 'border-gray-100 cursor-pointer hover:border-red-200 active:scale-[0.98] active:bg-gray-50',
      ].join(' ')}
      style={{
        WebkitTapHighlightColor: 'transparent',
        // content-visibility:auto faz o browser pular layout+paint
        // de cards fora do viewport. contain-intrinsic-size reserva
        // espaço pra não causar layout shift quando entram na tela.
        // Suporte: Chrome 85+, Safari 18+, Firefox 125+. Fallback:
        // browsers antigos ignoram (sem regressão).
        contentVisibility: 'auto',
        containIntrinsicSize: 'auto 152px',
      }}
    >
      {isSoldOut && (
        <div className="absolute top-2 right-2 z-10 bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded shadow-md inline-flex items-center gap-1">
          <i className="fas fa-ban" /> {t('Esgotado')}
        </div>
      )}
      <div className="flex-1 pr-2 min-w-0">
        <h3 className="font-bold text-gray-800 text-sm mb-1">{tr(product.name)}</h3>
        {product.is_highlighted && (
          <div className="mb-1">
            <span className="text-[10px] font-bold text-yellow-900 bg-yellow-400 px-1.5 py-0.5 rounded-sm uppercase tracking-wider inline-flex items-center gap-1">
              <i className="fas fa-star text-[9px]" /> {t('Recomendado')}
            </span>
          </div>
        )}
        {/* COMBO COM OFERTA DENTRO. Antes o desconto só aparecia depois de
            abrir o combo e chegar no seletor de sabores — quem passava o olho
            na lista seguia adiante sem saber. O "a partir de" é o gancho: dá o
            número que faz o cliente entrar. */}
        {product.has_promo_items && (
          <div className="mb-1.5">
            <span className="text-[10px] font-extrabold text-white bg-red-600 px-2 py-0.5 rounded uppercase tracking-wide inline-flex items-center gap-1 shadow-sm">
              <i className="fas fa-tags text-[9px]" />
              {product.promo_items_from
                ? t('Itens em promoção a partir de {v}', { v: `R$ ${Number(product.promo_items_from).toFixed(2).replace('.', ',')}` })
                : t('Itens em promoção')}
            </span>
          </div>
        )}
        {/* PROMOÇÃO SÓ PAGAMENTO ANTECIPADO: o preço do card é o de sempre;
            o selo diz quanto sai pagando pelo Pix online (o desconto entra no
            fechamento, quando o cliente escolhe o Pix). */}
        {product.prepaid_price ? (
          <div className="mb-1.5">
            <span className="text-[10px] font-extrabold text-white bg-emerald-600 px-2 py-0.5 rounded uppercase tracking-wide inline-flex items-center gap-1 shadow-sm">
              <i className="fas fa-qrcode text-[9px]" />
              {product.prepaid_size_name
                ? t('{s} por {v} no Pix antecipado', { s: tr(product.prepaid_size_name), v: `R$ ${Number(product.prepaid_price).toFixed(2).replace('.', ',')}` })
                : t('{v} no Pix antecipado', { v: `R$ ${Number(product.prepaid_price).toFixed(2).replace('.', ',')}` })}
            </span>
          </div>
        ) : null}
        {product.description && (
          <p className="text-xs text-gray-500 mb-3 leading-relaxed whitespace-pre-line line-clamp-3">
            {tr(product.description)}
          </p>
        )}
        <div className="flex flex-wrap gap-1 items-center mt-auto">
          <PriceDisplay product={product} />
        </div>
        {simple && !isSoldOut && (
          <div className="mt-2" onClick={(e) => e.stopPropagation()}>
            <InlineStepper product={product} />
          </div>
        )}
      </div>
      {product.image ? (
        <div className="w-24 h-24 ml-3 shrink-0 rounded-xl overflow-hidden bg-gray-100">
          <img
            src={product.image}
            loading="lazy"
            decoding="async"
            width={96}
            height={96}
            alt={tr(product.name)}
            className="w-full h-full object-cover"
            draggable={false}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).src = 'https://placehold.co/100x100?text=Sem+Foto';
            }}
          />
        </div>
      ) : (
        <div className="w-24 h-24 bg-gray-50 border border-gray-100 rounded-xl ml-3 flex items-center justify-center text-gray-300 shrink-0">
          <i className="fas fa-camera text-2xl opacity-40" />
        </div>
      )}
    </div>
  );
});

// ─── ProductCardsIsland: renderiza categorias inline ──────────────────
interface ProductCardsIslandProps {
  categories: MenuCategory[];
}

export function ProductCardsIsland({ categories }: ProductCardsIslandProps) {
  const { t, tr } = useI18n();
  const visibleCats = categories.filter((c) => c.items && c.items.length > 0);
  if (visibleCats.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500">
        <i className="fas fa-utensils text-4xl mb-3 opacity-50" />
        <p>{t('Cardápio em atualização.')}</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 mt-2">
      {visibleCats.map((cat, idx) => (
        <section
          key={cat.category_id}
          id={`delivery-cat-${idx}`}
          className="scroll-mt-32 lg:scroll-mt-20 mb-6 px-4 lg:px-6 pt-4"
        >
          <h2 className="font-bold text-lg lg:text-xl mb-3 px-1 border-l-4 border-red-500 pl-2 text-gray-800">
            {tr(cat.category_name)}
          </h2>
          {/* Uma coluna no celular (como sempre foi); 2 ou 3 colunas a partir
              de 1024px. `items-start` impede que um card de descrição longa
              estique os vizinhos da linha. */}
          <CollapsibleListGate
            items={cat.items.filter((p) => !p.is_hidden)}
            cap={CATEGORY_ITEM_CAP}
            wrapperClassName="lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-3 lg:items-start"
            moreButtonClassName="lg:col-span-full"
            moreLabel={(remaining) =>
              t('Ver mais {n} itens', { n: remaining })}
          >
            {(p) => (
              <ProductCard key={(p as ProductWithConfig).id} product={p as ProductWithConfig} />
            )}
          </CollapsibleListGate>
        </section>
      ))}
    </div>
  );
}
